/**
 * Pull replica - prefetch kluczowych GET-ów.
 * Auto-pull po logowaniu / starcie / online.
 *
 * Auto-pull ma TTL i opcjonalny jitter, żeby reconnect wielu klientów
 * nie walił w API w tej samej sekundzie. Ręczny pull (ConnectionStatus)
 * zawsze ściąga pełny zestaw.
 */

import { api } from "../../api/client";
import { saveToReplica } from "./replica";
import type { PullProgress, ReplicaKey } from "./types";

type PullItem = {
  key: ReplicaKey;
  fetch: () => Promise<{ data?: unknown; error?: unknown }>;
};

const PULL_ITEMS: PullItem[] = [
  {
    key: "animals:active",
    fetch: () => api.GET("/api/animals", { params: { query: { scope: "active" } } }),
  },
  {
    key: "animals:archived",
    fetch: () => api.GET("/api/animals", { params: { query: { scope: "archived" } } }),
  },
  {
    key: "tags",
    fetch: () => api.GET("/api/tags"),
  },
  {
    key: "dashboard",
    fetch: () => api.GET("/api/dashboard"),
  },
  {
    key: "dashboard:stats",
    fetch: () => api.GET("/api/dashboard/stats"),
  },
  {
    key: "todos",
    fetch: () => api.GET("/api/todos"),
  },
  {
    key: "finance:categories",
    fetch: () => api.GET("/api/finance/categories"),
  },
  {
    key: "finance:transactions",
    fetch: () => api.GET("/api/finance/transactions"),
  },
  {
    key: "birth-seasons:active",
    fetch: () => api.GET("/api/birth-seasons", { params: { query: { scope: "active" } } }),
  },
  {
    key: "breeding-seasons:active",
    fetch: () => api.GET("/api/breeding-seasons", { params: { query: { scope: "active" } } }),
  },
  {
    key: "free-official-tags",
    fetch: () =>
      api.GET("/api/free-official-tags", { params: { query: { include_assigned: true } } }),
  },
  {
    key: "tag-verifications:active",
    fetch: () => api.GET("/api/tag-verifications/active"),
  },
  {
    key: "birth-preferences",
    fetch: () => api.GET("/api/birth-preferences"),
  },
];

export const AUTO_PULL_MIN_INTERVAL_MS = 2 * 60 * 1000;
export const AUTO_PULL_JITTER_MAX_MS = 8000;
export const AUTO_PULL_LAST_AT_KEY = "offline:lastAutoPullAt";

export type PullResult = {
  success: number;
  failed: number;
  total: number;
  errors: Array<{ key: string; error: string }>;
  skipped?: boolean;
};

export type AutoPullOptions = {
  /** Losowe opóźnienie 0..AUTO_PULL_JITTER_MAX_MS — reconnect po awarii. */
  jitter?: boolean;
  /** Trigger source dla telemetry. */
  trigger?: "startup" | "online" | "manual";
};

function lastAutoPullAt(): number {
  const raw = localStorage.getItem(AUTO_PULL_LAST_AT_KEY);
  const value = raw ? Number(raw) : 0;
  return Number.isFinite(value) ? value : 0;
}

function markAutoPullDone(): void {
  localStorage.setItem(AUTO_PULL_LAST_AT_KEY, String(Date.now()));
}

function shouldSkipAutoPull(): boolean {
  const last = lastAutoPullAt();
  return last > 0 && Date.now() - last < AUTO_PULL_MIN_INTERVAL_MS;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function sendTelemetry(
  duration_ms: number,
  result: PullResult,
  trigger: string,
): Promise<void> {
  try {
    await api.POST("/api/telemetry/auto-pull", {
      body: {
        duration_ms,
        success_count: result.success,
        failed_count: result.failed,
        total_count: result.total,
        skipped: result.skipped || false,
        trigger,
      },
    });
  } catch {
    // Ignore telemetry errors (nie loguj żeby nie spamować console)
  }
}

/**
 * Pobierz wszystkie kluczowe dane i zapisz do replica.
 */
export async function pullReplica(
  onProgress?: (progress: PullProgress) => void,
): Promise<PullResult> {
  const result: PullResult = {
    success: 0,
    failed: 0,
    total: PULL_ITEMS.length,
    errors: [],
  };

  for (let i = 0; i < PULL_ITEMS.length; i++) {
    const item = PULL_ITEMS[i];

    onProgress?.({
      total: PULL_ITEMS.length,
      completed: i,
      current: item.key,
    });

    try {
      const response = await item.fetch();

      if (response.error) {
        result.failed += 1;
        result.errors.push({
          key: item.key,
          error: String(response.error),
        });
        console.warn(`[offlinePull] Failed to fetch ${item.key}:`, response.error);
      } else {
        await saveToReplica(item.key, response.data);
        result.success += 1;
        console.log(`[offlinePull] Saved ${item.key}`);
      }
    } catch (error) {
      result.failed += 1;
      result.errors.push({
        key: item.key,
        error: String(error),
      });
      console.error(`[offlinePull] Exception fetching ${item.key}:`, error);
    }
  }

  onProgress?.({
    total: PULL_ITEMS.length,
    completed: PULL_ITEMS.length,
  });

  console.log(
    `[offlinePull] Completed: ${result.success}/${result.total} success, ${result.failed} failed`,
  );

  return result;
}

/**
 * Ciche auto-pull (bez UI progress) — po logowaniu / starcie / online.
 * TTL + jitter ograniczają thundering herd; pełny zestaw replica zostaje.
 */
export async function autoPullReplica(options: AutoPullOptions = {}): Promise<PullResult> {
  const startTime = Date.now();
  const trigger = options.trigger || "unknown";

  if (!navigator.onLine) {
    console.log("[offlinePull] Skipping auto-pull (offline)");
    return { success: 0, failed: 0, total: 0, errors: [], skipped: true };
  }

  if (shouldSkipAutoPull()) {
    console.log("[offlinePull] Skipping auto-pull (recent pull)");
    return { success: 0, failed: 0, total: 0, errors: [], skipped: true };
  }

  if (options.jitter && AUTO_PULL_JITTER_MAX_MS > 0) {
    const delay = Math.floor(Math.random() * AUTO_PULL_JITTER_MAX_MS);
    if (delay > 0) {
      await sleep(delay);
    }
    if (!navigator.onLine || shouldSkipAutoPull()) {
      console.log("[offlinePull] Skipping auto-pull after jitter");
      return { success: 0, failed: 0, total: 0, errors: [], skipped: true };
    }
  }

  console.log("[offlinePull] Starting auto-pull...");
  const result = await pullReplica();
  const duration_ms = Date.now() - startTime;

  if (result.failed === 0) {
    markAutoPullDone();
  }

  if (result.failed > 0) {
    console.warn(
      `[offlinePull] Auto-pull completed with ${result.failed} failures:`,
      result.errors,
    );
  }

  // Send telemetry (fire and forget)
  void sendTelemetry(duration_ms, result, trigger);

  return result;
}
